import type { ReactNode } from "react";
import { ActivationPack } from "@/types/activation/activation-pack";
import { PrintOrderStatus } from "@/types/activation/print-order";
import { Agent } from "@/types/agent/agent";
import { Conversation } from "@/types/conversation/conversation";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/**
 * One independently-resolving slice of the dashboard console.
 *
 * Each band owns its own query, so a band that is still loading (or has
 * failed outright) never blanks the bands beside it. `isLoading` means
 * "nothing to show yet, draw the skeleton"; `isFetching` means "we already
 * have cached content on screen and are revalidating behind it".
 */
export interface ConsoleBand<TData> {
  data: TData;
  /** First load with no cached data — render the band skeleton. */
  isLoading: boolean;
  /** Background revalidation while cached data is on screen. */
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  /** True once this band has produced data at least once (success or empty). */
  isSettled: boolean;
}

// =====================================================================
// Band payloads
// =====================================================================

export interface GenieBandData {
  /** Every genie on the account, in the order My Genies shows them. */
  genies: Agent[];
  /** The genie the console is currently pointed at (never throws when absent). */
  selectedGenie: Agent | null;
  genieCount: number;
}

export interface KnowledgeBandData {
  /** First page of the selected genie's documents, newest first. */
  documents: DocumentWithMetadata[];
  documentCount: number;
  /** Documents untouched for longer than STALE_DOCUMENT_DAYS. */
  staleDocuments: DocumentWithMetadata[];
  /** ISO timestamp of the most recently updated document, if any. */
  lastUpdatedAt: string | null;
}

export type ReachChannelId = "web" | "qr" | "phone" | "embed" | "pack";

export interface ReachChannel {
  id: ReachChannelId;
  label: string;
  genieIcon: GenieIconName;
  live: boolean;
  /** Short live detail, e.g. the real phone number or the real link. */
  detail: string | null;
}

/**
 * `printOrderStatus` is deliberately three-valued. The only client path to a
 * print order today (`PrintOrderService.getOrCreate`) INSERTS a draft row, so
 * the dashboard must not call it on load — see
 * `docs/handoff/dashboard-print-order-read.md`. Until a read-only endpoint
 * exists the console reports `"unknown"` and the ranking skips that rule.
 */
export type PrintOrderState = PrintOrderStatus | "none" | "unknown";

export interface ReachBandData {
  packs: ActivationPack[];
  packCount: number;
  /** Public genie page, e.g. https://…/live-agent/acme-support. */
  publicUrl: string | null;
  /** Exact value encoded into the genie's real QR code. */
  qrValue: string | null;
  channels: ReachChannel[];
  scanCount: number;
  printOrderStatus: PrintOrderState;
}

export interface VoiceBandData {
  /** Most recent conversations for the selected genie. */
  conversations: Conversation[];
  conversationCount: number;
  /** Conversations the owner has not opened yet. */
  unreviewedCount: number;
  lastConversationAt: string | null;
}

export interface BuildStep {
  id: string;
  label: string;
  done: boolean;
  /** Live detail when done, e.g. the real voice name. */
  detail: string | null;
}

export interface BuildBandData {
  steps: BuildStep[];
  completedCount: number;
  totalCount: number;
  /** Other genies on the account, for the "what's next" preview. */
  otherGenies: Agent[];
}

// =====================================================================
// Ranking
// =====================================================================

export type NextActionRuleId =
  | "create-genie"
  | "add-knowledge"
  | "review-conversations"
  | "print-pack"
  | "share-genie"
  | "refresh-knowledge"
  | "healthy";

/**
 * Everything `resolveNextAction` is allowed to look at. Flat and serialisable
 * on purpose — the ranking is a pure function so it can be unit tested without
 * React, React Query, or a network.
 */
export interface NextActionInput {
  /** False while bands are still on their first load — render nothing yet. */
  isReady: boolean;
  genieCount: number;
  genieId: string | null;
  genieName: string | null;
  documentCount: number;
  staleDocumentCount: number;
  unreviewedConversationCount: number;
  packCount: number;
  firstPackId: string | null;
  printOrderStatus: PrintOrderState;
  scanCount: number;
}

export interface NextAction {
  ruleId: NextActionRuleId;
  /** `healthy` gets the same visual weight as `action` — never greyed out. */
  tone: "action" | "healthy";
  /** The single always-visible sentence. */
  sentence: string;
  buttonLabel: string;
  /** In-app route; verified against src/App.tsx. */
  to: string;
}

// =====================================================================
// Console
// =====================================================================

export interface GenieConsoleState {
  agentId: string | null;
  genie: ConsoleBand<GenieBandData>;
  knowledge: ConsoleBand<KnowledgeBandData>;
  reach: ConsoleBand<ReachBandData>;
  voice: ConsoleBand<VoiceBandData>;
  build: ConsoleBand<BuildBandData>;
  /** Raw inputs the "do this next" ranking consumes. */
  ranking: NextActionInput;
}

export interface UseGenieConsoleOptions {
  /**
   * Opt in to the print-order read. OFF by default because the only available
   * endpoint creates a draft row as a side effect of reading it.
   */
  enablePrintOrder?: boolean;
}

// =====================================================================
// Layout variants (HG-1225 exploration)
// =====================================================================

export type DashboardLayoutVariant = "spine" | "stage" | "filmstrip";

export const DASHBOARD_LAYOUT_VARIANTS: DashboardLayoutVariant[] = [
  "spine",
  "stage",
  "filmstrip",
];

export type LifecycleBandId = "brain" | "reach" | "voice" | "build";

export interface LifecycleBandView {
  id: LifecycleBandId;
  /** BRAIN / REACH / VOICE / BUILD */
  label: string;
  /** One-line promise for the stage under the label. */
  tagline: string;
  genieIcon: GenieIconName;
  tint: string;
  /** Live data sentence, e.g. "12 documents · updated 3 days ago". */
  stateLine: string;
  /** Small live rendering of the user's own content. */
  preview: ReactNode;
  linkTo: string;
  linkLabel: string;
  isLoading: boolean;
  isError: boolean;
}
